import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { User, ChevronDown, Menu, ChevronRight } from "lucide-react";
import NotificationsPopover from "../NotificationsPopover";
import ChatPopover from "../ChatPopover";
import { useIsMobile } from "../../hooks/use-mobile";
import { signOut } from "../../utilz/function";
import { SheetTrigger, Sheet, SheetContent } from "../ui/sheet";
import Icon from "../../assets/icons";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import Logo from "../../assets/images/logo.svg";

interface ProfileData {
  username: string;
  email: string;
  phone: string;
  address: string;
  lastLogin: string;
}

interface NavbarProps {
  profile: ProfileData;
}

const Navbar: React.FC<NavbarProps> = ({ profile }) => {
  const navigate = useNavigate();
  const {id} = useParams()
  const isMobile = useIsMobile();
  const location = useLocation();
  const currentPath = location.pathname;
  const [openSalesDropdown, setOpenSalesDropdown] = useState(false);
  const [openInventoryDropdown, setOpenInventoryDropdown] = useState(false);
  const salesDropdownRef = useRef<HTMLDivElement>(null);
  const inventoryDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when route changes
  useEffect(() => {
    setOpenSalesDropdown(false);
    setOpenInventoryDropdown(false);
  }, [location.pathname]);

  // Handle clicks outside the dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        salesDropdownRef.current &&
        !salesDropdownRef.current.contains(event.target as Node) &&
        inventoryDropdownRef.current &&
        !inventoryDropdownRef.current.contains(event.target as Node)
      ) {
        setOpenSalesDropdown(false);
        setOpenInventoryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const menuItems = [
    { label: "Dashboard", href: "/dashboard", active: currentPath === "/dashboard" },
    {
      label: "Sales",
      href: "#",
      active: 
      currentPath === "/dashboard/orders" || currentPath.startsWith("/dashboard/order/") ||
      currentPath === "/dashboard/customers" || currentPath.startsWith("/dashboard/customers/") || 
      currentPath === "/dashboard/discount" || currentPath.startsWith("/dashboard/discount/") || 
      currentPath === "/dashboard/transactions" || currentPath.startsWith("/dashboard/transactions/"),
      subItems: [
        { label: "Orders", 
          href: "/dashboard/orders", 
          active: currentPath === "/dashboard/orders" || currentPath.startsWith("/dashboard/order/")  },
        {
          label: "Customers",
          href: "/dashboard/customers",
          active: currentPath === "/dashboard/customers" || currentPath.startsWith("/dashboard/customers/")
        },
        {
          label: "Promotion and Discount",
          href: "/dashboard/discount",
          active: currentPath === "/dashboard/discount" || currentPath.startsWith("/dashboard/discount/"),
          
        },
        {
          label: "Transactions",
          href: "/dashboard/transactions",
          active: currentPath === "/dashboard/transactions" || currentPath.startsWith("/dashboard/transactions"),
          
        },
      ],
    },
    {
      label: "Inventory",
      href: "#",
      active: 
      currentPath === "/dashboard/suppliers" || currentPath.startsWith("/dashboard/supplier/") ||
      currentPath === "/dashboard/inventory" || currentPath.startsWith("/dashboard/inventory/")||
      currentPath === "/dashboard/regions" ||  currentPath.startsWith("/dashboard/regions/") ||
      currentPath === "/dashboard/requisition" || 
      currentPath.startsWith("/dashboard/requisition/") || 
      currentPath === "/dashboard/purchase-orders" ||
      currentPath.startsWith("/dashboard/purchase-orders/") ,
      subItems: [
        {
          label: "Supplier",
          href: "/dashboard/suppliers",
          active: currentPath === "/dashboard/suppliers" || currentPath.startsWith("/dashboard/supplier/"),
        },
        // {
        //   label: "Notifications",
        //   href: "/notifications",
        //   active: currentPath === "/notifications",
        // },
        {
          label: "Purchase Requisitions",
          href: "/dashboard/requisition",
          active: currentPath === "/dashboard/requisition" || currentPath.startsWith("/dashboard/requisition/"),
        },
        {
          label: "Inventory",
          href: "/dashboard/inventory",
          active: currentPath === "/dashboard/inventory" ||
          currentPath.startsWith("/dashboard/inventory/"),
        },
        {
          label: "Sourcing Regions",
          href: "/dashboard/regions",
          active: currentPath === "/dashboard/regions" ||currentPath.startsWith("/dashboard/regions/"),
        },
        {
          label: "Purchase Orders",
          href: "/dashboard/purchase-orders",
          active: currentPath === "/dashboard/purchase-orders" || currentPath.startsWith("/dashboard/purchase-orders/"),
        },
      ],
    },
    {
      label: "Users",
      href: "/dashboard/users",
      active: currentPath === "/dashboard/users",
    },
    {
      label: "Analytics & Reports",
      href: "/dashboard/analytics",
      active: currentPath === "/dashboard/analytics",
    },
  ];

  const handleSignOut = () => {
    signOut();
    navigate("/signin");
  };

  const MainMenu = () => (
    <div className="flex items-center space-x-6">
      {menuItems.map((item) => {
        if (item.subItems) {
          const isSales = item.label === "Sales";
          const isInventory = item.label === "Inventory";
          const isOpen = isSales ? openSalesDropdown : openInventoryDropdown;
          const setIsOpen = isSales ? setOpenSalesDropdown : setOpenInventoryDropdown;
          const dropdownRef = isSales ? salesDropdownRef : inventoryDropdownRef;

          return (
            <div key={item.label} className="relative group" ref={dropdownRef}>
              <button
                className={`text-[16px] font-medium transition-colors hover:text-veggie-600 flex items-center ${
                  item.active ? "text-[#047401]" : "text-gray-600"
                }`}
                onClick={() => setIsOpen(!isOpen)}
              >
                {item.label}
                <ChevronDown
                  className={`h-4 w-4 ml-1 transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isOpen && (
                <div className="absolute left-0 bg-white shadow-lg rounded-md py-2 w-48 z-10">
                  {item.subItems.map((subItem) => (
                    <Link
                      key={subItem.label}
                      to={subItem.href}
                      className={`block px-4 py-2 text-sm hover:bg-veggie-50 ${
                        subItem.active
                          ? "text-[#047401] bg-veggie-50"
                          : "text-gray-600"
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      {subItem.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return (
          <Link
            key={item.label}
            to={item.href}
            className={`text-[16px] font-medium transition-colors hover:text-veggie-600 ${
              item.active ? "text-[#047401]" : "text-gray-600"
            }`}
          >
            {item.label}
          </Link>
        );
      })}
    </div>
  );

  const MobileMenu = () => (
    <Sheet>
      <SheetTrigger asChild>
        <Button className="md:hidden">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-64">
        <Link
          to="/"
          className="font-bold text-2xl lg:hidden text-veggie-700 mr-8"
        >
          <img src={Logo} alt="logo" />
        </Link>
        <div className="flex flex-col space-y-4 pt-6">
          {menuItems.map((item) => {
            if (item.subItems) {
              const isSales = item.label === "Sales";
              const isOpen = isSales ? openSalesDropdown : openInventoryDropdown;
              const setIsOpen = isSales ? setOpenSalesDropdown : setOpenInventoryDropdown;

              return (
                <div key={item.label} className="px-4">
                  <button
                    className={`flex items-center justify-between w-full py-2 text-sm font-medium rounded-md transition-colors hover:bg-veggie-50 ${
                      item.active
                        ? "bg-veggie-50 text-[#047401]"
                        : "text-gray-600"
                    }`}
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    {item.label}
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-2 space-y-2">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.label}
                          to={subItem.href}
                          className={`block px-4 py-2 text-sm rounded-md hover:bg-veggie-50 ${
                            subItem.active
                              ? "bg-veggie-50 text-[#047401]"
                              : "text-gray-600"
                          }`}
                          onClick={() => setIsOpen(false)}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.label}
                to={item.href}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors hover:bg-veggie-50 ${
                  item.active ? "bg-veggie-50 text-[#047401]" : "text-gray-600"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <header className="border-b bg-[#E6E6E6] sticky top-0 z-10">
      <div className="container mx-auto py-7 flex items-center justify-between">
        <div className="flex items-center">
          {isMobile && <MobileMenu />}
          <Link
            to="/"
            className="font-bold text-2xl hidden lg:inline-block text-veggie-700 mr-8"
          >
            <img src={Logo} alt="logo" />
          </Link>
          {!isMobile && <MainMenu />}
        </div>

        <div className="flex items-center space-x-4">
          <NotificationsPopover />
          <ChatPopover />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-veggie-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-veggie-600" />
                </div>
                {!isMobile && (
                  <>
                    <span className="text-sm font-medium">
                      {profile?.username}
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* <DropdownMenuItem>
                <Link to="/dashboard/profile" className="flex w-full">
                  Profile
                </Link>
              </DropdownMenuItem> */}
              <DropdownMenuItem>
                <Link to="/dashboard/settings" className="flex w-full">
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Link to="#" className="flex w-full">
                  System Logs
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="flex gap-2">
                <Icon name="logoutIcon" />{" "}
                <button
                  className="text-[#F43F5E] text-sm"
                  type="button"
                  onClick={handleSignOut}
                >
                  Log out
                </button>{" "}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Navbar;